import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import { useTranslation } from 'react-i18next';
import { AlertBanner } from '@bloomays-lib/ui.shared';
import type { GlobalContractSummary } from '@bloomays-lib/utils.shared';

export type ContractsSummaryBannerProps = {
  summary: GlobalContractSummary;
  /** Optionnel : filtre la liste sur les documents « à signer » (bouton « Voir tout »). */
  onSeeAll?: () => void;
};

/**
 * Bandeau de synthèse global de l'écran « Contrats et avenants » (AC5) : nombre de documents à
 * signer, dont combien en retard le cas échéant, et sur combien de missions ils se répartissent.
 * Affiché uniquement s'il existe au moins un document « à signer » (`toSign > 0`) — jamais le
 * cas late-only (« 0 avenant · N en retard »), qui tromperait l'utilisateur et rendrait le CTA
 * « Voir tout » (filtre contrôlé `signingState = 'toSign'`) inopérant.
 */
const ContractsSummaryBanner = ({ summary, onSeeAll }: ContractsSummaryBannerProps) => {
  const { t } = useTranslation(['legalContracts']);

  if (summary.toSign === 0) return null;

  return (
    <AlertBanner
      tone="warning"
      icon={
        <IconSquare>
          <BoltRoundedIcon sx={{ fontSize: 18 }} aria-hidden />
        </IconSquare>
      }
      action={
        onSeeAll ? (
          <SeeAllButton type="button" onClick={onSeeAll}>
            {t('summaryBanner.seeAll')}
          </SeeAllButton>
        ) : undefined
      }
    >
      <TextColumn>
        <MainLine>
          <strong>{t('summaryBanner.pending', { count: summary.toSign })}</strong>
          {summary.late > 0 ? (
            <>
              <Separator aria-hidden> · </Separator>
              <LateEmphasis>{t('summaryBanner.late', { count: summary.late })}</LateEmphasis>
            </>
          ) : null}
        </MainLine>
        <SubLine>{t('summaryBanner.missions', { count: summary.missionsConcerned })}</SubLine>
      </TextColumn>
    </AlertBanner>
  );
};

export default ContractsSummaryBanner;

const IconSquare = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 30,
  height: 30,
  borderRadius: 8,
  backgroundColor: '#F6C563',
  color: '#8A5A00',
  flexShrink: 0,
}));

const TextColumn = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  textAlign: 'left',
  gap: 2,
  minWidth: 0,
  width: '100%',
}));

const MainLine = styled('span')(() => ({
  display: 'block',
  fontSize: 13,
  lineHeight: 1.3,
  textAlign: 'left',
}));

const Separator = styled('span')(() => ({
  fontWeight: 600,
}));

const LateEmphasis = styled('strong')(() => ({
  color: '#C0362C',
  fontWeight: 700,
}));

const SubLine = styled('span')(() => ({
  display: 'block',
  fontSize: 12,
  opacity: 0.85,
  textAlign: 'left',
}));

const SeeAllButton = styled('button')(({ theme }) => ({
  border: 'none',
  cursor: 'pointer',
  padding: '8px 18px',
  borderRadius: 999,
  fontSize: 13,
  fontWeight: 600,
  fontFamily: '"Poppins Medium", sans-serif',
  color: theme.palette.neutral.white,
  backgroundColor: theme.palette.neutral[90],
  transition: 'filter .15s',
  '&:hover': { filter: 'brightness(1.15)' },
}));
